import { fromJS } from 'immutable';
import { createStructuredSelector } from 'reselect';
import qs from 'querystring';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import IdentitiesService from 'services/IdentitiesService';
import { ENUM_IDENTITY_STATUS } from 'enum/profile';
import makeAction from 'utils/makeAction';
import parseCatch from 'utils/parseCatch';
import { usernameOrImpersonate } from 'utils/helpers';
import Base64ImageUtils from 'utils/base64ImageUtils';

import API from './Api';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

/**
 * State
 * @type {any}
 */
const initialState = fromJS({
  basicInfoDetails: {},
  countries: [],
  identities: [],
  accountStatus: ENUM_IDENTITY_STATUS.NO_STATUS,
});

/**
 * Action types
 * @type {string}
 */
const UPDATE_BASICINFO = 'cam4/studio/UPDATE_BASICINFO';
const UPDATE_COUNTRIES = 'cam4/studio/UPDATE_COUNTRIES';
const SET_IDENTITIES = 'cam4/studio/SET_IDENTITIES';
const ADD_IDENTITY = 'cam4/studio/ADD_IDENTITY';
const SET_ACCOUNT_STATUS = 'cam4/studio/SET_ACCOUNT_STATUS';

export const NAME = 'basicInfoDetails';

/**
 * Selectors
 * @param state
 * @returns {*}
 */
const basicInfoDetails = (state) => state[NAME].get('basicInfoDetails');
const accountStatus = (state) => state[NAME].get('accountStatus');
const countries = (state) => state[NAME].get('countries');
const identities = (state) => state[NAME].get('identities');

/**
 * Mutators
 * @type {function(...[*]): {type: *}}
 */
const setBasicInfoDetails = makeAction(UPDATE_BASICINFO, 'basicInfoDetails');
const updateCountries = makeAction(UPDATE_COUNTRIES, 'countries');
const setIdentities = makeAction(SET_IDENTITIES, 'identities');
const setAccountStatus = makeAction(SET_ACCOUNT_STATUS, 'accountStatus');

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_BASICINFO:
      return state.set('basicInfoDetails', fromJS(action.basicInfoDetails));
    case UPDATE_COUNTRIES:
      return state.set('countries', fromJS(action.countries));
    case SET_IDENTITIES:
      return state.set('identities', fromJS(action.identities));
    case ADD_IDENTITY:
      return state.update('identities', (list) => list.push(action.identity));
    case SET_ACCOUNT_STATUS:
      return state.set('accountStatus', fromJS(action.accountStatus));
    default:
      return state;
  }
}

function getBasicUserInfo(userName) {
  const URL = ApiService.substitute(API.user.basic_info, { userName: encodeURIComponent(userName) });

  return (dispatch) =>
    ApiService.get(URL)
      .then((res) => {
        const { data } = res;
        const { fanClubMigrationStatus, temptedFanClub } = data || {};

        const isMigrated = fanClubMigrationStatus === 'MIGRATED';
        const hasTemptedFanClub = Boolean(temptedFanClub) && fanClubMigrationStatus !== 'NOT_MIGRATED';

        const hasNewFanClub = hasTemptedFanClub || isMigrated;

        return dispatch(setBasicInfoDetails({ ...data, hasNewFanClub }));
      })
      .catch((err) => logger.error(err));
}

function addNewBasicInfo(userName, data) {
  const URL = ApiService.substitute(API.user.basic_info, { userName: encodeURIComponent(userName) });

  return (dispatch) =>
    ApiService.post(URL, { data }).then(
      () => dispatch(getBasicUserInfo(userName)),
      (error) => {
        parseCatch(error);
      }
    );
}

function changePerformerStatusToPending(username, studioOwnerName) {
  const data = { status: 'PENDING_ID_VERIFICATION' };
  const URL = ApiService.substitute(API.performer.update_status, {
    performerName: encodeURIComponent(username),
    studioOwnerName: encodeURIComponent(studioOwnerName),
  });

  return () => ApiService.put(URL, {
    data: qs.stringify(data),
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
  });
}

function saveIdentityOnBoardingImage(username, image, identityType, imageType) {
  return () => IdentitiesService.saveIdentityOnBoardingImage(
    username,
    identityType,
    {
      image,
      imageType,
    }
  );
}

function createIdentity(username, data) {
  return () => IdentitiesService.createIdentity(username, data);
}

function updateIdentity(username, type, data) {
  return () => IdentitiesService.updateIdentity(username, type, data);
}

function getIdentities({ username }) {
  return async(dispatch) => {
    const identitiesData = await IdentitiesService.getIdentities(username) || [];
    dispatch(setIdentities(identitiesData));

    return identitiesData;
  };
}

export function getAccountStatus({ username }) {
  const URL = ApiService.substitute(
    API.performer.status,
    { accountHolderUsername: encodeURIComponent(usernameOrImpersonate(username)) }
  );

  return (dispatch) => ApiService.get(URL).then(
    (results) => dispatch(setAccountStatus(results.data.status)),
    (error) => {
      if (error.response.status === StatusCodes.NOT_FOUND) {
        dispatch(setAccountStatus(ENUM_IDENTITY_STATUS.NO_IDENTITY));
      } else {
        parseCatch(error);
        dispatch(setAccountStatus(ENUM_IDENTITY_STATUS.ERROR));
      }
    }
  );
}

function blockedCountriesCall(username, blockedIds) {
  const data = {
    username,
    blockedIds,
  };

  const URL = ApiService.substitute(API.countries, { username: encodeURIComponent(username) });

  return () => ApiService.post(URL, {
    data: qs.stringify(data),
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
  });
}

function getCountries(username) {
  const URL = ApiService.substitute(API.countries, { username: encodeURIComponent(username) });

  return (dispatch) => ApiService.get(URL).then(
    (results) => dispatch(updateCountries(results.data.countries)),
    (error) => {
      parseCatch(error, true);
    }
  );
}

function isFaceDetected(username, imageType, image) {
  const binaryImage = image && Base64ImageUtils.toFile(image, 'capture.jpeg');
  const formData = new FormData();
  formData.append('username', username);
  formData.append('imageType', imageType);
  formData.append('image', binaryImage);
  const headers = { 'content-type': 'multipart/form-data' };
  const url = `${BASE_URL}/rest/v1.0/image-body-part-analyses`;

  return async() => {
    let response = undefined;
    try {
      response = await ApiService.sendRequestWithRetries(ApiService.post, url, {
        data: formData,
        headers,
      });
    } catch (error) {
      if (error.response.status >= StatusCodes.INTERNAL_SERVER_ERROR) {
        response = { data: { face: true } };
      }
      parseCatch(error);
    }

    return response;
  };
}

export const actionCreators = {
  getCountries,
  blockedCountriesCall,
  getBasicUserInfo,
  saveIdentityOnBoardingImage,
  changePerformerStatusToPending,
  getIdentities,
  getAccountStatus,
  createIdentity,
  addNewBasicInfo,
  updateIdentity,
  isFaceDetected,
};

export const selector = createStructuredSelector({
  basicInfoDetails,
  accountStatus,
  countries,
  identities,
});
